import { GridOptions, ICellRendererParams, ModelUpdatedEvent } from 'ag-grid-community';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { privateDecrypt } from 'crypto';
import moment from 'moment';
import { cxOneURLs, TopCategory } from 'src/app/cxone/dashboard/modules/top-categories/top-categories.model';
import { TopCategoriesService } from 'src/app/cxone/dashboard/modules/top-categories/top-categories.service';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CxoneTeamsComponent } from '../../toolbar-items/cxone-teams/cxone-teams.component';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-top-categories',
    templateUrl: './top-categories.component.html',
    styleUrls: ['./top-categories.component.scss'],
})
export class TopCategoriesComponent implements OnInit {
    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;
    @ViewChild('topCategoriesGrid') agGrid: AgGridAngular;

    showFilter = false;
    filterValue = "";

    noData = false;
    dataLoadSuccess = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";
    localeText = {};

    public teams = [];
    public gridOptions: GridOptions;
    public urls: cxOneURLs;

    private tableState = null;
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    private stateSaveTimer: any;
    private localizationDict: Map<string, string> = new Map<string, string>();
    private isDataLoading = false;
    private isFirstLoad = true;
    private volumeMax = 0;    //Maximum volume value; used to scale the volumes to generate volume bars
    private volLableWidth = 0;
    private rowData: TopCategory[] = [];

    constructor(
        private service: TopCategoriesService,
        private cvLocalizationService: CVLocalizationService,
        private gridService: AgGridHelperService,
        private configService: CvConfigurationService,
        private navService: NavigationService
    ) { }

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }

    async ngOnInit() {
        this.localeText = await this.gridService.getLocalizations();
        this.localizationDict = await this.cvLocalizationService.GetSection("Top_Categories_Module");
        await this.cvLocalizationService.GetSection("Common");

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");

        this.gridOptions = this.getGridOptions();
        this.setupModuleManager();
    }

    setupModuleManager() {
        //Set options
        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.loadData();
            },
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.Auto,
            canRefresh: () => !this.isDataLoading,
            onRefresh: (isAutoRefresh) => {
                if (!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            onExport: async (format) => await this.service.exportData(this.moduleKey, format),
        });

        this.moduleManager.addHeaderItem({
            name: 'Search',
            icon: 'search',
            click: () => this.toggleSearchArea()
        });

        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.addToolbarItem(CxoneTeamsComponent);

        this.moduleManager.customSettingsAdded = true;
    }

    getGridOptions(): GridOptions {
        return this.gridService.createGridOptions({
            defaultColDef: {
                minWidth: this.gridService.defaultMinWidth,
            },
            getRowId: ({ data: rowData }) => rowData.id,
            columnDefs: [
                {
                    headerName: this.localizationDict.get("Name") ?? "Name",
                    field: "title",
                    filter: "agTextColumnFilter",
                    sortable: true,
                },
                {
                    colId: "volume",
                    headerName: this.localizationDict.get("Volume") ?? "Volume",
                    field: "volume",
                    cellRenderer: "VolumeCellRenderer",
                    filter: "agNumberColumnFilter",
                    sortable: true,
                },
                {
                    headerName: this.localizationDict.get("Change") ?? "Change",
                    field: "changePercentage",
                    cellRenderer: "ChangeCellRenderer",
                    filter: "agTextColumnFilter",
                    sortable: true,
                }
            ],
            rowData: [],
            overlayNoRowsTemplate: this.configService.isCxOne ? `<div class="error-container" style="padding-top: 32px;">
                    <svg class=" mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text cvLocalizedText [langSection]="'Messaging_Admin_Message'"
                        [langKey]="'No_Results'">No results found
                    </p>
                </div>` : null,
            components: {
                VolumeCellRenderer: (params: ICellRendererParams) => {
                    //search URL variables
                    const envURL = this.navService.cxoneTokenUrls.niceBaseUrl;
                    let range = "";
                    let tnf = "";
                    const cat: string = params.data.id;

                    const svgNS = "http://www.w3.org/2000/svg";
                    //adjust range from 0-VolumeMax to 0-{ColWidth - volLabelWidth}
                    const volumeWidth = ((params.value * (params.column.getActualWidth() - (this.volLableWidth + 26)))
                        / this.volumeMax);
                    const volumeHeight = 12;

                    //create elements
                    const div = document.createElement('div');
                    const svg: SVGSVGElement = document.createElementNS(svgNS, 'svg') as SVGSVGElement;
                    const rect: SVGRectElement = document.createElementNS(svgNS, 'rect') as SVGRectElement;
                    const span = document.createElement('span');

                    const fromDate = this.moduleManager.moduleSettings?.selectedDateRange?.startDate;
                    fromDate.setHours(0, 0, 0, 0);
                    const toDate = this.moduleManager.moduleSettings?.selectedDateRange?.endDate;
                    toDate.setHours(0, 0, 0, 0);
                    toDate.addDays(1).addSeconds(-1);
                    const days = this.moduleManager?.moduleSettings.selectedDateRange.numberOfDays ?? 0;
                    if (fromDate == null || toDate == null || days === 0)
                        return "";

                    const name = this.moduleManager?.moduleSettings.selectedDateRange.name;
                    switch (name) {
                        case "Last 7 Days":
                            range = `3,${moment().valueOf()}`;
                            break;
                        case "Last 30 Days":
                            range = `7,${moment().valueOf()},30`;
                            break;
                        default:
                            range = `6,${moment().valueOf()},
                                ${moment(fromDate).valueOf()},
                                ${moment(toDate).valueOf()}`;
                            break;
                    }

                    //generate tnf
                    this.teams.forEach((team) => {
                        tnf = `${tnf}%2C${(team.name as string ).replace(" ", "%20")}`;
                    });

                    //set up div
                    div.classList.add("volumeCell", "cursor-pointer");
                    div.style.height = "100%";
                    div.style.width = `${volumeWidth + this.volLableWidth + 26}px`;
                    div.style.display = "flex";
                    div.style.alignItems = "center";
                    div.addEventListener("click", (event) => {
                        window.open(`${envURL}/search/#/search?page=1&sort=1startTime22&range=${range}&tnf=${tnf}&cat=${cat}:A`, "_blank");
                    });

                    //set up volume svg
                    svg.style.fill = "#007CBE";
                    svg.style.width = `${volumeWidth}px`;
                    svg.viewBox.baseVal.x = 0;
                    svg.viewBox.baseVal.y = 0;
                    svg.viewBox.baseVal.width = volumeWidth;
                    svg.viewBox.baseVal.height = volumeHeight;
                    //set up svg's rect
                    rect.x.baseVal.value = 0;
                    rect.y.baseVal.value = 0;
                    rect.height.baseVal.value = volumeHeight;
                    rect.width.baseVal.value = volumeWidth;
                    //set up volume label span
                    span.style.paddingLeft = "10px";
                    span.style.fontSize = "13px";
                    span.innerText = Math.round(params.value).toFixed(0);

                    //append children
                    svg.appendChild(rect);
                    div.appendChild(svg);
                    div.appendChild(span);

                    return div;
                },
                ChangeCellRenderer: (params: ICellRendererParams) => {
                    const svgNS = "http://www.w3.org/2000/svg";
                    const div = document.createElement('div');
                    const svg: SVGSVGElement = document.createElementNS(svgNS, 'svg') as SVGSVGElement;
                    const path: SVGUseElement = document.createElementNS(svgNS, 'use') as SVGUseElement;
                    const span = document.createElement('span');

                    div.style.height = "100%";
                    div.style.display = "flex";
                    div.style.flexDirection = "row";
                    div.style.alignItems = "center";

                    svg.setAttributeNS(null, "width", "16px");
                    svg.setAttributeNS(null, "height", "16px");
                    svg.setAttributeNS(null, "viewBox", "0 0 16 16");
                    svg.setAttributeNS(null, 'fill', '#93A4AA');

                    if (params.value < 0)
                        path.setAttributeNS(null, 'href', "../App_Themes/CXone/icons/down-arrow.svg#down-arrow");
                    else
                        path.setAttributeNS(null, 'href', "../App_Themes/CXone/icons/up-arrow.svg#up-arrow");

                    span.innerText = `${Math.abs(params.value).toFixed(1).replace(/[.,]0+$/, "")}%`;
                    span.style.marginLeft = "10px";

                    svg.appendChild(path);
                    div.appendChild(svg);
                    div.appendChild(span);

                    return div;
                }
            },
            onModelUpdated: (event: ModelUpdatedEvent) => {
                if (event.api.getDisplayedRowCount() === 0)
                    event.api.showNoRowsOverlay();
                else
                    event.api.hideOverlay();
            },
            onColumnResized: (event) => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    if (event.columns.findIndex((col) => col['colId'] === "volume") !== -1)
                        requestAnimationFrame(() => this.agGrid?.api.redrawRows());
                    this.updateTableState();
                }
            },
            onGridReady: (event) => {
                if (this.rowData) {
                    this.agGrid?.api.setGridOption('rowData', this.rowData);
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.gridService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the tableState is null.
                    this.updateTableState();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
            },
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnVisible: () => this.updateTableState(),
            onGridSizeChanged: (event) => {
                if (this.isResizeWaiting) {
                    this.gridService.sizeColumnsSmart(event);
                    this.updateTableState();
                } else if (this.moduleManager.container.isFullscreen) {
                    this.gridService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            onFilterChanged: () => this.queueStateSave(),
            localeText: this.localeText,
            suppressDragLeaveHidesColumns: true,
            suppressNoRowsOverlay: true,
        });
    }

    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }

        this.stateSaveTimer = setTimeout(async () => {
            this.stateSaveTimer = false;
            if (this.agGrid?.api)
                this.tableState = this.gridService.getGridState(this.agGrid?.api);
            if (await this.moduleManager.saveState('tableState')) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500);
    };

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.stateRestoreInProgress)
            return;

        // generate state from grid
        const tableState = this.gridService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    restoreColumnState() {
        if (!this.tableState|| !this.agGrid?.api) {
            return;
        }

        this.gridService.loadGridState(this.agGrid?.api, this.tableState);
    };

    async loadData(isAutoRefresh: boolean = false) {
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;

        if (!isAutoRefresh) {
            this.moduleManager.showLoader = true;
        }

        this.volumeMax = 0;

        const topCatDTO = await this.service.loadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
        this.teams = await this.service.getCXoneTeamNames(this.moduleKey);

        if (!topCatDTO) {
            this.rowData = [];
            this.teams = [];
        }
        else {
            this.rowData = topCatDTO.rowData;
            this.rowData.forEach((e) => {
                if (e.volume > this.volumeMax)
                    this.volumeMax = e.volume;
            });

            this.urls = topCatDTO.urls;
        }

        //calculate max volume label width
        const span = document.createElement("span");
        span.style.font = "Lato-Regular";
        span.style.fontFamily = "sans-serif";
        span.style.fontSize = "13px";
        span.style.height = "auto";
        span.style.width = "auto";
        span.style.position = "absolute";
        span.style.whiteSpace = "no-wrap";
        span.innerText = this.volumeMax.toString();
        document.body.appendChild(span);
        this.volLableWidth = Math.ceil(span.clientWidth);
        document.body.removeChild(span);

        this.noData = this.rowData.length === 0;
        this.dataLoadSuccess = !this.noData;

        if (this.agGrid?.api) {
            this.agGrid?.api.setGridOption('rowData', this.rowData);
            this.agGrid?.api.redrawRows();
        }

        this.isDataLoading = false;
        this.isFirstLoad = false;
        this.moduleManager.setAutoRefreshReady();
        this.moduleManager.showLoader = false;
    }

    toggleSearchArea() {
        this.showFilter = !this.showFilter;
        if (!this.showFilter) {
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        }
        else {
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }

    applyFilter() {
        if (!!this.agGrid?.api)
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
    }

    clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Change input focus to the search bar.
     */
    async focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.configService.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }
}
